import { useEffect, useRef, useState } from 'react';
import { Trans } from '@lingui/macro';

import { ZIndex } from '@rover/constants/src/styles/ZIndex';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import MQ from '@rover/kibble/styles/MediaQuery';
import useTheme from '@rover/kibble/themes/useTheme';
import useMatchMedia from '@rover/react-lib/src/hooks/useMatchMedia';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';

import { AvatarType, Size } from '../../Avatar/Avatar';
import StackedAvatar from '../../StackedAvatar/StackedAvatar';
import { ShortlistContactButtonClickEvent, ShortlistOpenEvent } from '../analyticsEvents';
import { MAX_PREVIEW_AVATARS, MIN_PREVIEW_AVATARS, MULTI_CONTACT_ROUTE } from '../constants';
import useShortlist from '../hooks/useShortlist';
import { useShortlistPreviewTarget } from '../ShortlistPreviewTargetContext';

import NewBadge from './NewBadge';

export const buildPreviewImageUrls = (count: number, loadedImageUrls: string[]): string[] => {
  const slots = Math.max(MIN_PREVIEW_AVATARS, Math.min(count, MAX_PREVIEW_AVATARS));
  const loaded = loadedImageUrls.slice(0, slots);
  return [...loaded, ...Array.from({ length: slots - loaded.length }, () => '')];
};

/** How far short of the release point the backdrop has already reached full opacity. */
export const BACKDROP_FADE_OFFSET_PX = 50;

/** How much scrolling the backdrop fades in over, measured back from that full-opacity point. */
export const BACKDROP_FADE_DISTANCE_PX = 150;

/**
 * Backdrop opacity for a given distance, in px, between the scroll container's bottom edge and
 * the viewport bottom, the closer to the bottom, the more opaque the backdrop.
 */
export const computeBackdropOpacity = (distanceToRelease: number): number => {
  const progress = 1 - (distanceToRelease - BACKDROP_FADE_OFFSET_PX) / BACKDROP_FADE_DISTANCE_PX;
  return Math.min(1, Math.max(0, progress));
};

const ShortlistPreview = (): JSX.Element | null => {
  const isMobile = useMatchMedia(MQ.MOBILE.getBreakpointString());
  const prefersReducedMotion = useMatchMedia('(prefers-reduced-motion: reduce)');
  const { isVisible, count, getSitters, setIsModalVisible } = useShortlist();
  const { registerTarget } = useShortlistPreviewTarget();
  const theme = useTheme();
  const [isAnimating, setIsAnimating] = useState(false);

  const prevCount = useRef(count);
  useEffect(() => {
    if (count > prevCount.current && !prefersReducedMotion) {
      setIsAnimating(true);
    }
    prevCount.current = count;
  }, [count, prefersReducedMotion]);

  // The backdrop fades in as the bar nears its sticky release point at the end of the scroll
  // container, so the grey band only appears where it replaces the page's own background.
  // Opacity is written straight to the node: this runs once per animation frame while scrolling,
  // and routing it through state would re-render the whole bar that often.
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const backdropRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const container = wrapperRef.current?.parentElement;
    if (!container) {
      return undefined;
    }

    let frame = 0;
    const update = (): void => {
      frame = 0;
      const backdrop = backdropRef.current;
      if (backdrop) {
        const distanceToRelease = container.getBoundingClientRect().bottom - window.innerHeight;
        backdrop.style.opacity = String(computeBackdropOpacity(distanceToRelease));
      }
    };
    const schedule = (): void => {
      if (!frame) {
        frame = window.requestAnimationFrame(update);
      }
    };

    update();
    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);

    return () => {
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
      window.removeEventListener('scroll', schedule);
      window.removeEventListener('resize', schedule);
    };
    // `count` changes the bar's height, which moves the container's bottom edge.
  }, [isVisible, count]);

  if (!isVisible) {
    return null;
  }

  const isEmpty = count === 0;

  const pulseAnimationStyles = {
    '@keyframes shortlistPulse': {
      '0%': { transform: 'scale(1)', backgroundColor: theme.background.primary },
      '30%': { transform: 'scale(1.05)', backgroundColor: theme.background.secondary },
      '60%': { transform: 'scale(0.98)', backgroundColor: theme.background.primary },
      '100%': { transform: 'scale(1)', backgroundColor: theme.background.primary },
    },
    animation: 'shortlistPulse 0.4s ease-in-out',
  };

  const loadedImageUrls = getSitters()
    .map((sitter) => sitter.defaultImage.medium || sitter.defaultImage.small)
    .filter(Boolean);
  const imageUrls = buildPreviewImageUrls(count, loadedImageUrls);

  const handleLinkPress = (): void => {
    emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
    setIsModalVisible(true);
  };

  const handleButtonPress = (): void => {
    if (isEmpty) {
      emitAnalyticsEvent(new ShortlistOpenEvent({ source: 'preview', count }));
      setIsModalVisible(true);
    } else {
      new ShortlistContactButtonClickEvent({ source: 'preview', count }).fireCrossPageEvent();
    }
  };

  return (
    <Box
      ref={wrapperRef}
      position="sticky"
      bottom="0"
      width="100%"
      // On desktop, sit just below the FilterDropdown z-index. On mobile the
      // dropdowns aren't rendered, and the fixed Filters button needs to stay on top.
      zIndex={[String(ZIndex.NAVBAR_FIXED.toNumber()), '999']}
      display="flex"
      justifyContent="center"
      p={['0x', '4x']}
      testID="shortlist-preview"
      sx={{ pointerEvents: 'none', borderRadius: '10x' }}
    >
      <Box
        ref={backdropRef}
        position="absolute"
        top="0"
        right="0"
        bottom="0"
        left="0"
        background="secondary"
        testID="shortlist-preview-backdrop"
        sx={{ opacity: 0 }}
      />
      <Flex
        position="relative"
        width="100%"
        maxWidth={['100%', '600px']}
        flexDirection={['column', 'row']}
        alignItems={['stretch', 'center']}
        justifyContent="space-between"
        gap="4x"
        background="primary"
        borderRadius={['10x', 'round']}
        variant="elevation_mid"
        px="4x"
        py="4x"
        sx={{
          pointerEvents: 'auto',
          borderTopLeftRadius: ['16px', undefined],
          borderTopRightRadius: ['16px', undefined],
          ...(isAnimating ? pulseAnimationStyles : {}),
        }}
        webProps={{ onAnimationEnd: () => setIsAnimating(false) }}
      >
        <Flex flexDirection="row" alignItems="center" gap="3x" minWidth="0">
          {/* Decorative: the adjacent button already states the count, and the
              stack is padded with empty slots so it can misrepresent it. */}
          <Box
            ref={registerTarget}
            display="inline-flex"
            flexShrink="0"
            webProps={{ 'aria-hidden': true }}
          >
            <StackedAvatar
              imageUrls={imageUrls}
              avatarType={AvatarType.Person}
              size={isMobile ? Size.ExtraSmall : Size.Small}
              avatarPropsOverrideCallback={(imageUrl) =>
                imageUrl === ''
                  ? {
                      borderColor: theme.colors.border.primary,
                      borderStyle: 'dashed',
                    }
                  : {
                      borderColor: theme.colors.neutral.white,
                      borderStyle: 'solid',
                    }
              }
            />
          </Box>
          {isEmpty ? (
            <Flex flexDirection="column">
              <Flex flexDirection="row" gap="2x">
                <Text size="200" fontWeight="semibold">
                  <Trans>Your contact list</Trans>
                </Text>
                <NewBadge />
              </Flex>
              <Text>
                <Trans>Add sitters and message them all at once</Trans>
              </Text>
            </Flex>
          ) : (
            <Button variant="link" onPress={handleLinkPress} sx={{ color: 'text.primary' }}>
              <Trans>View contact list ({count})</Trans>
            </Button>
          )}
        </Flex>
        <Box width={['100%', 'auto']} flexShrink="0">
          <Button
            variant="primary"
            fullWidth
            href={!isEmpty ? MULTI_CONTACT_ROUTE : undefined}
            onPress={handleButtonPress}
          >
            {isEmpty ? <Trans>View</Trans> : <Trans>Contact all</Trans>}
          </Button>
        </Box>
      </Flex>
    </Box>
  );
};

export default ShortlistPreview;
